import React from "react";
import { Button, useTheme } from "@mui/material";
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { ShieldCheckIcon, StarIcon, UserGroupIcon, UserIcon } from "@heroicons/react/16/solid";
import { PaginationButtons, RowsPerPageSelector } from "../../utils/paginationUtils";
import useFocusTrap from "../../hooks/useFocusTrap";
import { roleLabel } from "../../utils/organisationApi";

// Same shell as the panelist modals: header with close button, body, footer actions
// fixedHeight keeps the dialog the same size whatever it holds, so tabs and loading never make it jump;
// the content then scrolls inside. compact tightens the padding for dense, detail-style dialogs.
export const OrgModal = ({ isOpen, onClose, titleId, title, children, footer, maxWidth = "max-w-lg", fixedHeight = false, compact = false, headerActions = null, above = false }) => {
  const theme = useTheme();
  const modalRef = useFocusTrap(isOpen, onClose);
  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 flex items-start justify-center ${above ? "z-[1400]" : "z-50"} bg-black bg-opacity-70`}>
      <div
        ref={modalRef}
        className={`modal-content relative w-full ${maxWidth} bg-white rounded-lg shadow-xl ${fixedHeight ? "h-[min(86vh,720px)]" : "max-h-[85vh]"} flex flex-col overflow-hidden max-sm:mx-2 my-auto`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div
          className={`flex justify-between items-center ${compact ? "py-3" : "py-4"} px-5 border-b`}
          style={{ backgroundColor: theme.palette.primary.main, borderColor: theme.palette.secondary.v4 }}
        >
          <h2 id={titleId} className="min-w-0 font-semibold">
            {title}
          </h2>
          <div className="flex shrink-0 items-center gap-2">
            {headerActions}
            <button
              onClick={onClose}
              aria-label="Close modal"
              className="inline-flex items-center justify-center rounded-full bg-black text-white h-6 w-6 hover:bg-gray-800 transition-colors"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div
          className={`flex flex-col ${compact ? "px-4 sm:px-5 py-4 gap-3" : "px-5 sm:px-6 py-6 gap-5"} ${fixedHeight ? "flex-1 min-h-0 overflow-hidden" : "overflow-y-auto"}`}
          style={{ color: theme.palette.text.main }}
        >
          {children}
        </div>
        {footer && (
          <div className="px-5 py-3 border-t flex justify-end gap-3" style={{ borderColor: theme.palette.secondary.v4 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export const ErrorBox = ({ children }) =>
  children ? <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">{children}</div> : null;

export const PrimaryButton = ({ children, danger = false, sx, ...props }) => {
  const theme = useTheme();
  const color = danger ? theme.palette.error.main : theme.palette.info.main;
  return (
    <Button
      {...props}
      sx={{
        backgroundColor: color,
        color: theme.palette.text.light,
        textTransform: "none",
        minWidth: "max-content",
        "&:hover": { backgroundColor: danger ? "#991b1b" : "#4171cf" },
        "&.Mui-disabled": { backgroundColor: theme.palette.secondary.v3, color: theme.palette.text.dark },
        ...sx,
      }}
    >
      {children}
    </Button>
  );
};

// Outlined partner to PrimaryButton for secondary calls to action
export const SecondaryButton = ({ children, sx, ...props }) => {
  const theme = useTheme();
  return (
    <Button
      {...props}
      variant="outlined"
      sx={{
        color: theme.palette.info.main,
        borderColor: theme.palette.info.main,
        backgroundColor: theme.palette.primary.main,
        textTransform: "none",
        minWidth: "max-content",
        "&:hover": { backgroundColor: "#eef3fb", borderColor: theme.palette.info.main },
        "&.Mui-disabled": { color: theme.palette.text.dark, borderColor: theme.palette.secondary.v3, backgroundColor: theme.palette.secondary.v5 },
        ...sx,
      }}
    >
      {children}
    </Button>
  );
};

export const TextButton = ({ children, sx, ...props }) => {
  const theme = useTheme();
  return (
    <Button
      {...props}
      variant="text"
      sx={{ color: theme.palette.text.main, textTransform: "none", minWidth: "max-content", ...sx }}
    >
      {children}
    </Button>
  );
};

// Confirmation with an optional "type the name" guard for destructive actions
export const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  busyLabel,
  busy,
  error,
  danger = true,
  confirmText,
  typedValue,
  onTypedChange,
}) => {
  const theme = useTheme();
  const needsTyping = Boolean(confirmText);
  const typedMatches = !needsTyping || (typedValue || "").trim() === confirmText;
  return (
    <OrgModal
      isOpen={isOpen}
      onClose={busy ? () => {} : onClose}
      titleId="org-confirm-title"
      title={title}
      maxWidth="max-w-md"
      above
      footer={
        <>
          <TextButton onClick={onClose} disabled={busy}>
            Cancel
          </TextButton>
          <PrimaryButton danger={danger} onClick={onConfirm} disabled={busy || !typedMatches}>
            {busy ? busyLabel || "Working..." : confirmLabel}
          </PrimaryButton>
        </>
      }
    >
      <div>{message}</div>
      {needsTyping && (
        <div>
          <label htmlFor="org-confirm-input" className="block text-sm font-medium mb-2">
            Type <span className="font-bold">{confirmText}</span> to confirm
          </label>
          <input
            id="org-confirm-input"
            type="text"
            value={typedValue}
            onChange={(e) => onTypedChange(e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{ borderColor: theme.palette.secondary.v4 }}
            autoComplete="off"
          />
        </div>
      )}
      <ErrorBox>{error}</ErrorBox>
    </OrgModal>
  );
};

// Same pill language as the interview status chips: tinted fill, matching border, small label
const pillClass =
  "inline-flex items-center gap-1.5 whitespace-nowrap align-middle rounded-full border px-2.5 py-0.5 text-xs font-medium";

const ROLE_TONES = {
  owner: "border-blue-300 bg-blue-100 text-blue-800",
  admin: "border-indigo-300 bg-indigo-100 text-indigo-800",
  hr: "border-yellow-400 bg-yellow-50 text-yellow-800",
  panelist: "border-gray-300 bg-gray-100 text-gray-700",
  super_admin: "border-violet-300 bg-violet-100 text-violet-800",
};

const ROLE_ICONS = {
  owner: StarIcon,
  admin: ShieldCheckIcon,
  hr: UserGroupIcon,
  panelist: UserIcon,
  super_admin: ShieldCheckIcon,
};

export const RoleBadge = ({ role }) => {
  const Icon = ROLE_ICONS[role] || UserIcon;
  return (
    <span className={`${pillClass} ${ROLE_TONES[role] || ROLE_TONES.panelist}`}>
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      {roleLabel(role)}
    </span>
  );
};

const STATUS_TONES = {
  active: "border-green-300 bg-green-100 text-green-800",
  pending: "border-amber-300 bg-amber-100 text-amber-800",
  invited: "border-amber-300 bg-amber-100 text-amber-800",
  requested: "border-blue-300 bg-blue-100 text-blue-800",
  expired: "border-red-300 bg-red-100 text-red-800",
  suspended: "border-violet-300 bg-violet-100 text-violet-800",
  deleted: "border-red-300 bg-red-100 text-red-800",
  removed: "border-gray-300 bg-gray-100 text-gray-700",
  trial_active: "border-blue-300 bg-blue-100 text-blue-800",
  trial_expired: "border-red-300 bg-red-100 text-red-800",
  past_due: "border-red-300 bg-red-100 text-red-800",
  cancel_scheduled: "border-amber-300 bg-amber-100 text-amber-800",
  cancelled: "border-gray-300 bg-gray-100 text-gray-700",
  payment_failed: "border-red-300 bg-red-100 text-red-800",
  ended: "border-gray-300 bg-gray-100 text-gray-700",
  no_plan: "border-gray-300 bg-gray-100 text-gray-700",
};

const STATUS_LABELS = {
  active: "Active",
  pending: "Pending",
  invited: "Invited",
  requested: "Requested",
  expired: "Expired",
  suspended: "Suspended",
  deleted: "Deleted",
  removed: "Removed",
  trial_active: "Trial",
  trial_expired: "Trial ended",
  past_due: "Payment failed",
  cancel_scheduled: "Cancelling",
  cancelled: "Cancelled",
  payment_failed: "Payment failed",
  ended: "Ended",
  no_plan: "No plan",
};

export const StatusBadge = ({ status }) => {
  if (!status) return "—";
  const label = STATUS_LABELS[status] || status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span className={`${pillClass} ${STATUS_TONES[status] || STATUS_TONES.removed}`}>
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" aria-hidden="true" />
      {label}
    </span>
  );
};

// Page header bar used by every organisation screen
export const OrgPageHeader = ({ title, subtitle, children }) => {
  const theme = useTheme();
  return (
    <div
      className="w-full flex flex-wrap justify-between items-center gap-2 py-5 px-4 sm:px-6 max-sm:pl-12 border border-l-0 border-r-0 shadow-md"
      style={{
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.text.main,
        borderColor: theme.palette.secondary.v4,
      }}
    >
      <div>
        <h1 className="font-semibold text-2xl">{title}</h1>
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
};

// Heading inside a Settings tab. The page itself carries the OrgPageHeader.
export const SettingsPanelHeader = ({ title, children }) => {
  const theme = useTheme();
  return (
    <div className="w-full flex flex-wrap justify-between items-start gap-3 px-6 max-md:px-4 pt-6">
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
};

// White card that groups one block of settings
export const Section = ({ title, description, children, dense = false }) => {
  const theme = useTheme();
  return (
    <section
      className={`border rounded-md ${dense ? "p-4" : "p-5 sm:p-6"}`}
      style={{ backgroundColor: theme.palette.primary.main, borderColor: theme.palette.secondary.v4 }}
    >
      {title && <h3 className={`font-semibold ${dense ? "text-base" : "text-lg"}`}>{title}</h3>}
      {description && <p className={`text-sm text-accent-gray-light ${dense ? "mt-0.5" : "mt-1"}`}>{description}</p>}
      <div className={title || description ? (dense ? "mt-3" : "mt-4") : ""}>{children}</div>
    </section>
  );
};

// Prev / page numbers / next, matching the panelist table footer
export const Pager = ({ currentPage, totalPages, onPageChange, rowsPerPage, onRowsPerPageChange }) => {
  const theme = useTheme();
  return (
    <div className="mt-auto w-full flex flex-col justify-center items-center px-3 py-3 sm:px-6">
      <div className="relative flex max-lg:flex-col w-full justify-center items-center">
        {onRowsPerPageChange && (
          <RowsPerPageSelector
            rowsPerPage={rowsPerPage}
            handleChangeRowsPerPage={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
          />
        )}
        <nav aria-label="Pagination" className="flex flex-wrap justify-center items-center gap-2">
          <button
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            className="relative inline-flex items-center border rounded px-2 py-2 bg-white hover:bg-accent-blue hover:text-white text-xs sm:text-sm"
            style={{ borderColor: theme.palette.secondary.v4 }}
            disabled={currentPage === 1}
          >
            <ChevronLeftIcon aria-hidden="true" className="h-5 w-5" />
            <span className="hidden xs:inline ml-1">Prev</span>
          </button>
          <PaginationButtons
            currentPage={currentPage}
            totalPages={totalPages}
            handlePageChange={onPageChange}
            theme={theme}
          />
          <button
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            className="relative inline-flex items-center rounded px-2 py-2 bg-white hover:bg-accent-blue hover:text-white text-xs sm:text-sm border"
            style={{ borderColor: theme.palette.secondary.v4 }}
            disabled={currentPage === totalPages}
          >
            <span className="hidden xs:inline mr-1">Next</span>
            <ChevronRightIcon aria-hidden="true" className="h-5 w-5" />
          </button>
        </nav>
      </div>
    </div>
  );
};

// Short-lived success message shown under the page header
export const Notice = ({ children, onDismiss }) =>
  children ? (
    <div
      role="status"
      className="mx-6 max-md:mx-4 mt-4 p-3 items-center bg-green-50 border border-green-200 text-green-800 rounded flex justify-between items-start gap-3"
    >
      <span>{children}</span>
      {onDismiss && (
        <button onClick={onDismiss} aria-label="Dismiss" className="shrink-0">
          <XMarkIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  ) : null;

export const Spinner = ({ label }) => (
  <div className="mx-auto px-6 text-center mt-16">
    <div className="spinner-border animate-spin inline-block w-8 h-8 border-2 border-t-4 border-black rounded-full" />
    <p>{label}</p>
  </div>
);
